import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Calendar } from "lucide-react";
import { ContentGallerySection } from "./ContentGallerySection";
import { CampaignTasksSection } from "@/components/campaign-tasks/CampaignTasksSection";
import { DeliverablesSection } from "./DeliverablesSection";
import {
  type CampaignInfluencer,
  type CampaignOperationsTabsProps,
  OperationsTabsHeader,
  InfluencersTabContent,
  EditInfluencerDetailsDialog,
  DeleteInfluencerDialog,
} from "./operations";

export type { CampaignInfluencer, CampaignOperationsTabsProps };

type Props = CampaignOperationsTabsProps;

export const CampaignOperationsTabs: React.FC<Props> = ({
  influencers,
  campaignId,
  onAddInfluencer,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<string>("influencers");
  const [editingInfluencer, setEditingInfluencer] =
    useState<CampaignInfluencer | null>(null);
  const [editDeliverables, setEditDeliverables] = useState("");
  const [editFee, setEditFee] = useState<number | "">("");
  const [deletingInfluencer, setDeletingInfluencer] =
    useState<CampaignInfluencer | null>(null);

  // Edit details mutation
  const editInfluencerMutation = useMutation({
    mutationFn: async ({
      influencerId,
      deliverables,
      feeCommitted,
    }: {
      influencerId: string;
      deliverables: string;
      feeCommitted: number | null;
    }) => {
      const { error } = await supabase
        .from("campaign_influencers")
        .update({
          deliverables,
          fee_committed: feeCommitted,
        })
        .eq("campaign_id", campaignId)
        .eq("influencer_id", influencerId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["campaign-influencers", campaignId],
      });
      toast({
        title: "Success",
        description: "Influencer details updated successfully",
      });
    },
    onError: (error) => {
      console.error("Error updating influencer details:", error);
      toast({
        title: "Error",
        description: "Failed to update influencer details",
        variant: "destructive",
      });
    },
  });

  // Delete influencer mutation
  const deleteInfluencerMutation = useMutation({
    mutationFn: async (influencerId: string) => {
      const { error } = await supabase
        .from("campaign_influencers")
        .delete()
        .eq("campaign_id", campaignId)
        .eq("influencer_id", influencerId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["campaign-influencers", campaignId],
      });
      toast({
        title: "Success",
        description: "Influencer removed from campaign successfully",
      });
    },
    onError: (error) => {
      console.error("Error removing influencer:", error);
      toast({
        title: "Error",
        description: "Failed to remove influencer from campaign",
        variant: "destructive",
      });
    },
  });

  // Status change mutation
  const updateStatusMutation = useMutation({
    mutationFn: async ({
      influencerId,
      status,
    }: {
      influencerId: string;
      status: string;
    }) => {
      const { error } = await supabase
        .from("campaign_influencers")
        .update({
          status: status as
            | "contacted"
            | "commercials_awaited"
            | "negotiating"
            | "cancelled"
            | "confirmed"
            | "content_in_progress"
            | "content_shared"
            | "content_rejected"
            | "content_approved"
            | "payment_in_pipeline"
            | "completed",
        })
        .eq("campaign_id", campaignId)
        .eq("influencer_id", influencerId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["campaign-influencers", campaignId],
      });
      toast({
        title: "Success",
        description: "Influencer status updated successfully",
      });
    },
    onError: (error) => {
      console.error("Error updating status:", error);
      toast({
        title: "Error",
        description: "Failed to update influencer status",
        variant: "destructive",
      });
    },
  });

  const handleOpenEdit = (inf: CampaignInfluencer) => {
    setEditingInfluencer(inf);
    setEditDeliverables(
      inf.deliverables === "Not specified" ? "" : inf.deliverables
    );
    setEditFee(
      inf.feeCommitted !== undefined && inf.feeCommitted !== null
        ? inf.feeCommitted
        : ""
    );
  };

  const handleSaveEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingInfluencer) return;

    const feeValue = editFee === "" ? null : Number(editFee);
    editInfluencerMutation.mutate(
      {
        influencerId: editingInfluencer.id,
        deliverables: editDeliverables,
        feeCommitted: feeValue,
      },
      {
        onSuccess: () => {
          setEditingInfluencer(null);
        },
      }
    );
  };

  const handleConfirmDelete = () => {
    if (!deletingInfluencer) return;
    deleteInfluencerMutation.mutate(deletingInfluencer.id, {
      onSuccess: () => {
        setDeletingInfluencer(null);
      },
    });
  };

  const handleStatusChange = (influencerId: string, newStatus: string) => {
    updateStatusMutation.mutate({ influencerId, status: newStatus });
  };

  const handleViewProfile = (influencerId: string) => {
    navigate(`/influencers/${influencerId}`);
  };

  return (
    <div className="p-6 border border-slate-200 rounded-2xl">
      <h2 className="text-2xl font-bold mb-4 font-['Hanken_Grotesk',sans-serif]">
        Campaign Operations
      </h2>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <OperationsTabsHeader activeTab={activeTab} />

        <TabsContent value="influencers" className="mt-6">
          <InfluencersTabContent
            campaignId={campaignId}
            influencers={influencers}
            onAddInfluencer={onAddInfluencer}
            onViewProfile={handleViewProfile}
            onStatusChange={handleStatusChange}
            isUpdatingStatus={updateStatusMutation.isPending}
            onEdit={handleOpenEdit}
            onDelete={setDeletingInfluencer}
          />
        </TabsContent>

        <TabsContent value="deliverables" className="mt-6">
          <DeliverablesSection campaignId={campaignId} />
        </TabsContent>

        <TabsContent value="content" className="mt-6">
          <ContentGallerySection campaignId={campaignId} />
        </TabsContent>

        <TabsContent value="notes" className="mt-6">
          <CampaignTasksSection campaignId={campaignId} />
        </TabsContent>

        <TabsContent value="calendar" className="mt-6">
          <div className="text-center py-8 text-gray-500">
            <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <h4 className="text-lg font-medium mb-2">Campaign Calendar</h4>
            <p>Calendar integration coming soon</p>
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Details Dialog */}
      <EditInfluencerDetailsDialog
        isOpen={!!editingInfluencer}
        influencer={editingInfluencer}
        deliverables={editDeliverables}
        onDeliverablesChange={setEditDeliverables}
        fee={editFee}
        onFeeChange={setEditFee}
        onClose={() => setEditingInfluencer(null)}
        onSave={handleSaveEdit}
        isPending={editInfluencerMutation.isPending}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteInfluencerDialog
        isOpen={!!deletingInfluencer}
        influencer={deletingInfluencer}
        onClose={() => setDeletingInfluencer(null)}
        onConfirm={handleConfirmDelete}
        isPending={deleteInfluencerMutation.isPending}
      />
    </div>
  );
};

export default CampaignOperationsTabs;
